import { useLayoutEffect } from 'react';
import { MIN_PROMPT_FONT_SIZE_PX, PROMPT_TARGET_MAX_ROWS } from './formativeMultipleChoiceConstants';

const RESIZE_DEBOUNCE_MS = 150;

/**
 * Shrink-to-fit for MMC prompt bar text: scale font-size down when the rendered text
 * exceeds a target row count, floored at a minimum legible size. Single-pass — computes
 * the ratio needed to hit the target directly from one overflow measurement rather than
 * an iterative shrink-and-remeasure loop.
 */

/**
 * @param {object} params
 * @param {number} params.measuredHeightPx - rendered scrollHeight of the text at fontSizePx.
 * @param {number} params.lineHeightPx - computed line-height at fontSizePx.
 * @param {number} params.fontSizePx - computed font-size the measurement was taken at.
 * @param {number} params.targetMaxRows - row count the text should fit within before scaling.
 * @param {number} params.minFontSizePx - absolute floor; never scale below this.
 * @returns {{ fontSizePx: number, lineHeightPx: number } | null} new sizes, or null if no
 *   scaling is needed (text already fits within the target row count).
 */
export function computeShrinkToFitFontSizePx({
  measuredHeightPx,
  lineHeightPx,
  fontSizePx,
  targetMaxRows,
  minFontSizePx,
}) {
  const targetMaxHeightPx = targetMaxRows * lineHeightPx;
  if (measuredHeightPx <= targetMaxHeightPx) {
    return null;
  }

  // Height scales roughly with fontSize^2, not fontSize: shrinking the font both
  // shortens each line AND fits more characters per line (in a fixed-width wrapper),
  // so row count itself drops as font-size drops. sqrt(scale) targets the row count
  // directly instead of overshooting into a smaller-than-necessary font.
  const scale = Math.sqrt(targetMaxHeightPx / measuredHeightPx);
  const newFontSizePx = Math.min(fontSizePx, Math.max(minFontSizePx, fontSizePx * scale));
  const newLineHeightPx = lineHeightPx * (newFontSizePx / fontSizePx);

  return { fontSizePx: newFontSizePx, lineHeightPx: newLineHeightPx };
}

/** Reads the base (unscaled) computed font-size/line-height and scrollHeight for a text node. */
function measureBaseTextMetrics(span) {
  span.style.fontSize = '';
  span.style.lineHeight = '';
  const computed = window.getComputedStyle(span);
  return {
    fontSizePx: parseFloat(computed.fontSize),
    lineHeightPx: parseFloat(computed.lineHeight),
    measuredHeightPx: span.scrollHeight,
  };
}

function applyShrinkToFit(span) {
  if (!span) return;
  const { fontSizePx, lineHeightPx, measuredHeightPx } = measureBaseTextMetrics(span);
  const scaled = computeShrinkToFitFontSizePx({
    measuredHeightPx,
    lineHeightPx,
    fontSizePx,
    targetMaxRows: PROMPT_TARGET_MAX_ROWS,
    minFontSizePx: MIN_PROMPT_FONT_SIZE_PX,
  });
  if (scaled) {
    span.style.fontSize = `${scaled.fontSizePx}px`;
    span.style.lineHeight = `${scaled.lineHeightPx}px`;
  }
}

/**
 * Re-measures and re-applies shrink-to-fit on text/visibility/resize/containerHeightPx changes.
 * containerHeightPx is a dependency-only parameter that re-triggers measurement once --mmc-h
 * lands — child effects run before parent ones and would otherwise measure against 100vh fallback.
 */
export function useShrinkToFitPromptText(text, isVisible, spanRef, containerHeightPx) {
  useLayoutEffect(() => {
    if (!isVisible) return undefined;
    applyShrinkToFit(spanRef.current);

    let debounceTimer = null;
    const onResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => applyShrinkToFit(spanRef.current), RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('resize', onResize);
    };
  }, [text, isVisible, spanRef, containerHeightPx]);
}
